// ─── RATTACHER : la même feuille dans les deux sens ───────────────
// « Cette modale devrait être identique à celle dont on parlait juste avant »
// (Cédric, 11/08). Un seul composant, deux orientations :
//   • depuis une PRÉVISION → on choisit l'opération du compte qui l'honore ;
//   • depuis une OPÉRATION → on choisit la prévision qu'elle honore.
// Deux écrans jumeaux auraient divergé au premier ajustement.
//
// Trois règles portées ici :
//   1. l'app PROPOSE, l'utilisateur DÉCIDE — rien n'est écrit sans son geste ;
//   2. la proposition en tête est celle qui se détache VRAIMENT (cf.
//      `preselection`) ; deux candidats à égalité, on ne tranche pas ;
//   3. le montant est modifiable — c'est ce qui rend le n↔n possible : une
//      opération peut n'honorer une prévision que pour une part.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Feuille, CalqueChoix, LigneSwipe, useUI, useT, frJMA } from '@mop/core';
import { formatEuro, montantOperation, dateEffective } from './services/calculs';
import { emojiCategorie, cleCategorie } from './data/categories';
import { preselection } from './services/propositions';
import { candidatsElargis } from './services/propositions';
import {
  FILTRES_DEFAUT, filtrerCandidates, trierCandidates, TRIS_RATTACHEMENT,
} from './services/selectionOperations';
import BarreRechercheRattachement from './BarreRechercheRattachement';
import FeuilleFiltresRattachement from './FeuilleFiltresRattachement';

export default function FeuilleRattachement({
  visible, source, candidats = [], depuisOccurrence = true,
  dejaRattaches = [], soldee = false, onDetacher, onSolder,
  // Pour ÉLARGIR au-delà des propositions : il faut alors TOUTES les
  // opérations, pas seulement celles que l'heuristique a retenues.
  operations = [], rattachements = [], comptes = [],
  onValider, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [choisi, setChoisi] = useState(null);
  const [montant, setMontant] = useState('');
  // Ce que l'utilisateur TAPE dans le champ %, tel quel : sans ça, « 33, »
  // serait réécrit en « 33 » entre deux frappes.
  // ⚠️ `null` = jamais touché (affiche le calculé) ; `''` = VIDÉ par
  // l'utilisateur (affiche vide) — sinon le champ est ineffaçable (16/08).
  const [pctSaisi, setPctSaisi] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [filtres, setFiltres] = useState({ ...FILTRES_DEFAUT });
  const [tri, setTri] = useState({ tri: 'date', sens: -1 });
  const [panneau, setPanneau] = useState(null);

  // Tant que l'utilisateur ne cherche rien, il voit les PROPOSITIONS —
  // courtes et pertinentes. Dès qu'il touche la recherche ou les filtres, on
  // lui rend la main sur TOUTES les opérations : c'est lui qui juge, plus
  // l'heuristique. Le basculement est dit sous la barre, jamais silencieux.
  const elargi = depuisOccurrence
    && (!!recherche.trim() || JSON.stringify(filtres) !== JSON.stringify(FILTRES_DEFAUT));

  const liste = useMemo(() => {
    if (!elargi) return candidats;
    const ops = trierCandidates(filtrerCandidates({
      operations, rattachements, ligneId: source?.ligneId, mois: source?.mois, filtres, recherche,
      reference: Math.abs(source?.prevu || 0),
    }), { ...tri, comptes });
    return candidatsElargis({ occurrence: source, operations: ops, rattachements, toutes: operations });
  }, [elargi, candidats, operations, rattachements, source, filtres, recherche, tri, comptes]);

  const suggere = useMemo(() => preselection(candidats), [candidats]);
  // À l'ouverture, on met en avant la suggestion — sans la valider. Le bouton
  // reste à presser : c'est la différence entre proposer et décider.
  useEffect(() => {
    if (!visible) return;
    setChoisi(suggere ? cleDe(suggere, depuisOccurrence) : null);
    setMontant('');
    setPctSaisi(null);
  }, [visible, suggere, depuisOccurrence]);

  const retenu = useMemo(
    () => liste.find(c => cleDe(c, depuisOccurrence) === choisi) || null,
    [liste, choisi, depuisOccurrence],
  );
  // Montant par défaut : ce que la cible attend, borné à ce que la source peut
  // encore donner. Couvre le 1 pour 1 sans rien saisir.
  // 100 % de l'opération, ou 100 % de ce qu'il lui RESTE (Cédric, 15/08).
  // Avant, la part était bornée par ce que l'échéance attend : une opération
  // de 846 € sur une échéance de 252 € proposait 252 €, et les 594 € restants
  // disparaissaient sans qu'on sache où. C'est à l'utilisateur d'ajuster s'il
  // veut éviter le dépassement — pas à l'écran de décider pour lui.
  const defaut = retenu
    ? (depuisOccurrence ? retenu.reste : Math.abs(source?.montant || 0))
    : 0;

  // Le TOTAL de l'opération sert de référence au pourcentage. Depuis une
  // occurrence, l'opération est le candidat retenu ; dans l'autre sens, c'est
  // la source.
  const totalOperation = depuisOccurrence
    ? Math.abs(retenu?.operation?.montant || 0)
    : Math.abs(source?.montant || 0);
  const valeur = montant === '' ? defaut : (parseFloat(montant.replace(',', '.')) || 0);

  // Le MONTANT reste la source de vérité — c'est lui qu'on enregistre. Le
  // pourcentage n'est qu'une autre façon de le saisir : on le recalcule à
  // chaque rendu plutôt que de le stocker, sinon les deux divergeraient au
  // premier arrondi.
  const pct = totalOperation > 0
    ? Math.round((valeur / totalOperation) * 1000) / 10
    : 0;
  const changerPct = (v) => {
    const n = parseFloat(String(v).replace(',', '.'));
    if (!totalOperation || !Number.isFinite(n)) { setPctSaisi(v); return; }
    setPctSaisi(v);
    setMontant(String(Math.round(totalOperation * (n / 100) * 100) / 100));
  };
  const valide = !!retenu && valeur > 0.005;

  return (
    <Feuille visible={visible} titre={t('finances.actRattacher')} emoji="🔗" hauteur={0.85}
      sansScroll onFermer={onFermer}
      action={{ icone: '✓', label: t('commun.valider'), disabled: !valide,
        onPress: () => valide && onValider?.(retenu, Math.round(valeur * 100) / 100) }}>

      {/* Ce qu'on rattache : rappelé en tête, sinon on choisit à l'aveugle. */}
      {!!source && (
        <View style={s.source}>
          <View style={s.sourceTexte}>
            <Text style={s.sourceLbl} numberOfLines={1}>
              {depuisOccurrence
                ? `${emojiCategorie(source.ligne?.categorie)} ${source.ligne?.libelle || ''}`
                : (source.libelle || t(cleCategorie(source.categorie)))}
            </Text>
            {/* Le MOIS, en tete : tout ce qui suit ne concerne que lui —
                « deja rattache a ce mois », le cadenas, les parts. Rien ne le
                disait, il fallait s'en souvenir (Cedric, 14/08). */}
            {depuisOccurrence && !!source?.mois && (
              <Text style={s.sourceMois}>{libelleMois(source.mois, t)}</Text>
            )}
            <Text style={s.sourceSous}>
              {depuisOccurrence
                ? `${t('finances.reste')} ${formatEuro(manqueDe(source))}`
                : `${frJMA(dateEffective(source))}  ·  ${montantOperation(source.montant, source.sens)}`}
            </Text>
          </View>
          {/* Fermer / rouvrir le mois : une ICONE dans la carte, pleine
              hauteur (Cedric, 13/08). Le cadenas montre l'ETAT — 🔓 ouvert,
              🔒 clos — et l'appui le bascule. Le libelle d'accessibilite dit
              l'ACTION, lui : une icone d'etat ne la dit pas. */}
          {depuisOccurrence && !!onSolder && (
            <TouchableOpacity style={[s.cadenas, soldee && s.cadenasOn]} activeOpacity={0.7}
              onPress={() => onSolder(!soldee)}
              accessibilityRole="button"
              accessibilityLabel={t(soldee ? 'finances.rouvrirMois' : 'finances.rienDePlusMois')}>
              {/* ✅ = valide ; ☑️ = a valider. Le cadenas parlait de
                  MECANISME ; la coche parle de ce qu'on FAIT (Cedric, 15/08). */}
              <Text style={s.cadenasIc}>{soldee ? '✅' : '☑️'}</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* La barre ne défile PAS : sans elle sous la main, il faut remonter
          toute la liste pour élargir la recherche (leçon du 12/08). */}
      {depuisOccurrence && (
        <BarreRechercheRattachement style={s.barre}
          recherche={recherche} onRecherche={setRecherche}
          filtres={filtres} triActif={tri.tri !== 'date'}
          onOuvrirFiltres={() => setPanneau('filtres')}
          onOuvrirTri={() => setPanneau('tri')} />
      )}

      <ScrollView style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 26 }}>
        {/* CE QUI EST DÉJÀ LÀ, avant ce qu'on peut ajouter (Cédric, 13/08) :
            sans cette liste, on décide d'un complément sans voir ce qu'il
            complète.
            ⚠️ DANS le ScrollView : hors de lui, ce bloc n’avait pas la marge
            latérale du contenu et son titre se décalait de celui d'en
            dessous (Cédric, 14/08). */}
        {depuisOccurrence && dejaRattaches.length > 0 && (
          <>
            <Text style={s.titre}>{t('finances.dejaRattacheMois')}</Text>
            {dejaRattaches.map(r => (
              /* Le balayage plutôt qu’une croix : c’est le principe app-wide,
                 et la ligne retrouve la même largeur que les rattachables. */
              <LigneSwipe key={r.id} espacement={8} enabled
                onSupprimer={onDetacher ? () => onDetacher(r) : undefined}>
                <View style={[s.item, s.itemFait]}>
                  <Text style={s.coche}>✓</Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={s.itemLbl} numberOfLines={1}>
                      {r.operation?.libelle || t(cleCategorie(r.operation?.categorie))}
                    </Text>
                    <Text style={s.itemSous} numberOfLines={1}>
                      {r.operation ? frJMA(dateEffective(r.operation)) : ''}
                    </Text>
                  </View>
                  <Text style={s.itemMontant}>{formatEuro(r.montant)}</Text>
                </View>
              </LigneSwipe>
            ))}
          </>
        )}

      <Text style={s.titre}>
        {depuisOccurrence ? t('finances.rattachables') : t('finances.aRattacher')}
      </Text>
      {/* Dire QUELLE liste on regarde : « aucune opération » ne veut pas dire
          la même chose selon qu'on voit les propositions ou tout. */}
      <Text style={s.mode}>
        {elargi ? t('finances.modeToutes') : t('finances.modeProposees')}
      </Text>

      {liste.length === 0 ? (
        <Text style={s.vide}>
          {depuisOccurrence ? t('finances.aucuneRattachable') : t('finances.aucuneOpRattachee')}
        </Text>
      ) : liste.map(c => {
        const cle = cleDe(c, depuisOccurrence);
        const on = cle === choisi;
        const o = depuisOccurrence ? c.operation : c.occurrence;
        return (
          <TouchableOpacity key={cle} style={[s.item, on && s.itemOn]} activeOpacity={0.7}
            onPress={() => { setChoisi(on ? null : cle); setMontant(''); setPctSaisi(null); }}>
            <Text style={s.coche}>{on ? '◉' : '○'}</Text>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[s.itemLbl, on && s.itemLblOn]} numberOfLines={1}>
                {depuisOccurrence
                  ? (o.libelle || t(cleCategorie(o.categorie)))
                  : `${emojiCategorie(o.ligne?.categorie)} ${o.ligne?.libelle || ''}`}
              </Text>
              <Text style={s.itemSous} numberOfLines={1}>
                {depuisOccurrence ? frJMA(dateEffective(o)) : o.mois}
                {/* « Déjà vu » : l'utilisateur l'a confirmé par le passé. C'est
                    l'information la plus utile de la liste, elle est dite. */}
                {c.dejaVu ? `  ·  ⭐ ${t('finances.dejaRattacheAilleurs')}` : ''}
              </Text>
            </View>
            <Text style={s.itemMontant}>
              {formatEuro(depuisOccurrence ? c.reste : c.manque)}
            </Text>
          </TouchableOpacity>
        );
      })}

      {/* Le montant N'APPARAÎT qu'une fois une cible choisie : le demander
          avant n'aurait aucun sens, et le pré-remplir non plus. */}
      {!!retenu && (
        <>
          <Text style={s.titre}>{t('finances.montantPrevu')}</Text>
          {/* Montant et POURCENTAGE liés dans les deux sens (Cédric, 15/08) :
              « 84 € » ne dit pas si l'opération est presque entièrement
              affectée ici ou à peine entamée ; « 10 % » ne dit pas combien
              d'euros. Les deux ensemble, chacun modifiable. */}
          <View style={s.rangeeMontant}>
            <TextInput style={[s.input, { flex: 1 }]} value={montant}
              onChangeText={(v) => { setMontant(v.replace(/[^0-9.,]/g, '')); setPctSaisi(null); }}
              keyboardType="decimal-pad"
              placeholder={formatEuro(defaut)} placeholderTextColor={UI.faint} />
            {totalOperation > 0 && (
              <View style={s.zonePct}>
                <TextInput style={s.inputPct}
                  value={pctSaisi !== null ? pctSaisi : String(pct)}
                  onChangeText={(v) => changerPct(v.replace(/[^0-9.,]/g, ''))}
                  keyboardType="decimal-pad" />
                <Text style={s.pctSigne}>%</Text>
              </View>
            )}
          </View>
          <Text style={s.aide}>{t('finances.montantPartAide')}</Text>
        </>
      )}

      </ScrollView>

      {/* CALQUES et non <Feuille> : on est déjà dans une Feuille, deux
          <Modal> imbriquées figent l'écran. */}
      <FeuilleFiltresRattachement visible={panneau === 'filtres'} filtres={filtres}
        comptes={comptes} reference={Math.abs(source?.prevu || 0)}
        onAppliquer={setFiltres} onFermer={() => setPanneau(null)} />

      <CalqueChoix visible={panneau === 'tri'} titre={t('finances.tri')} emoji="↕"
        onFermer={() => setPanneau(null)}>
        {TRIS_RATTACHEMENT.map(x => (
          <TouchableOpacity key={x.id} style={[s.item, tri.tri === x.id && s.itemOn]}
            activeOpacity={0.7}
            onPress={() => { setTri(v => (v.tri === x.id ? { ...v, sens: -v.sens } : { tri: x.id, sens: -1 })); }}>
            <Text style={s.itemLbl}>{t(x.cle)}</Text>
            {tri.tri === x.id && <Text style={s.coche}>{tri.sens < 0 ? '▼' : '▲'}</Text>}
          </TouchableOpacity>
        ))}
      </CalqueChoix>
    </Feuille>
  );
}

// Clé stable d'un candidat, dans un sens comme dans l'autre.
const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

// Meme rendu que la fiche de prevision : « Mai 2026 », pas « 2026-05 ».
const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

const cleDe = (c, depuisOccurrence) => (depuisOccurrence
  ? c?.operation?.id
  : `${c?.occurrence?.ligneId}|${c?.occurrence?.mois}`);

// Ce qu'une occurrence attend encore.
const manqueDe = (occ) => Math.max(0, Math.abs(occ?.prevu || 0) - Math.abs(occ?.reel || 0));

const makeStyles = (UI) => StyleSheet.create({
  // `sansScroll` supprime le padding du corps : les blocs FIXES portent leur
  // marge, le contenu défilant a la sienne dans le contentContainerStyle.
  barre: { marginHorizontal: 16 },
  mode: { fontSize: 11.5, color: UI.faint, marginBottom: 8, fontWeight: '700' },
  // `overflow: hidden` pour que le fond du cadenas respecte l'arrondi.
  source: { flexDirection: 'row', alignItems: 'stretch', backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, marginBottom: 10, marginHorizontal: 16, marginTop: 16, overflow: 'hidden' },
  sourceTexte: { flex: 1, minWidth: 0, padding: 12 },
  cadenas: { paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center', borderLeftWidth: 0.5, borderLeftColor: UI.border },
  cadenasOn: { backgroundColor: UI.bg },
  cadenasIc: { fontSize: 20 },
  sourceLbl: { fontSize: 15, fontWeight: '800', color: UI.ink },
  sourceMois: { fontSize: 12.5, color: UI.accent, marginTop: 3, fontWeight: '800' },
  sourceSous: { fontSize: 12.5, color: UI.muted, marginTop: 2, fontWeight: '700' },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 16, marginBottom: 6 },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18 },
  item: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11, paddingHorizontal: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, marginBottom: 8 },
  // Dans un `LigneSwipe`, l'espacement est porté par le wrapper : sinon les
  // boutons d'action dépassent de la marge (documenté dans le Core).
  itemOn: { borderColor: UI.accent, borderWidth: 1.5 },
  coche: { fontSize: 16, color: UI.accent, width: 20 },
  itemLbl: { fontSize: 14, color: UI.ink, fontWeight: '700' },
  itemLblOn: { fontWeight: '800' },
  itemSous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  itemMontant: { fontSize: 14, fontWeight: '800', color: UI.ink },
  input: { backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 11, paddingHorizontal: 12, fontSize: 16, fontWeight: '800', color: UI.ink },
  rangeeMontant: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  zonePct: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12, paddingVertical: 11 },
  inputPct: { minWidth: 46, textAlign: 'right', fontSize: 16, fontWeight: '800', color: UI.ink },
  pctSigne: { fontSize: 15, fontWeight: '800', color: UI.muted },
  aide: { fontSize: 11.5, color: UI.faint, lineHeight: 16, marginTop: 6 },
  // Bordure verte ET marge a zero : dans un `LigneSwipe`, l'espacement est
  // porte par le wrapper, sinon les boutons d'action depassent de la marge.
  itemFait: { borderColor: UI.ok || '#2e7d32', backgroundColor: UI.card, marginBottom: 0 },
  detacher: { fontSize: 15, color: UI.muted, marginLeft: 4, fontWeight: '700' },
});
